import { ApiRequestError, NetworkError } from '@/lib/api';
import { cn } from '@/lib/cn';

import { Button } from './Button';
import { Icon } from './Icon';

export interface ErrorStateProps {
  /** Whatever the query threw. Classified here, not by the calling screen. */
  error: unknown;
  /** Wire this to the query's `refetch`. Omit when a retry cannot help. */
  onRetry?: () => void;
  /** Overrides the derived headline. Use when the screen has better context. */
  title?: string;
  bare?: boolean;
  className?: string;
}

interface Described {
  title: string;
  detail: string;
  retryable: boolean;
}

/**
 * Turn a thrown thing into something a person can act on.
 *
 * This is the only place in the app that decides what an error *means*. A
 * screen that writes its own copy for a 403 will get it subtly different from
 * the next screen, and "Forbidden" on its own has never helped anyone.
 */
export function describeError(error: unknown): Described {
  if (error instanceof NetworkError) {
    return {
      title: 'Could not reach the server',
      detail: 'The request never arrived. Check your connection and try again.',
      retryable: true,
    };
  }

  if (error instanceof ApiRequestError) {
    if (error.isUnauthenticated) {
      return {
        title: 'Your session has ended',
        detail: 'Sign in again to carry on.',
        retryable: false,
      };
    }
    if (error.isForbidden) {
      return {
        title: 'Not available to your account',
        detail: error.message || 'Your role does not have access to this.',
        retryable: false,
      };
    }
    if (error.isNotFound) {
      return {
        title: 'Not found',
        detail: 'It may have been deleted, or it may belong to someone else.',
        retryable: false,
      };
    }
    if (error.isRateLimited) {
      return {
        title: 'Too many attempts',
        detail: 'Wait a moment before trying again.',
        retryable: true,
      };
    }
    if (error.isValidation) {
      return {
        title: 'That request was rejected',
        detail: error.message || 'Some of the values sent were not valid.',
        retryable: false,
      };
    }
    return {
      title: 'Something went wrong',
      detail: error.message || `The server returned ${error.status}.`,
      retryable: true,
    };
  }

  return {
    title: 'Something went wrong',
    detail: error instanceof Error ? error.message : 'An unexpected error stopped this loading.',
    retryable: true,
  };
}

export function ErrorState({ error, onRetry, title, bare = false, className }: ErrorStateProps) {
  const described = describeError(error);

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        !bare && 'rounded-lg border border-danger/25 bg-danger-soft/50',
        className,
      )}
    >
      <span className="grid size-11 place-items-center rounded-full bg-danger-soft text-danger-ink">
        <Icon name="alert" size={19} />
      </span>
      <div className="max-w-sm">
        <h3 className="font-display text-section font-semibold tracking-tight">
          {title ?? described.title}
        </h3>
        <p className="mt-1 text-small text-muted">{described.detail}</p>
      </div>
      {onRetry && described.retryable ? (
        <Button size="sm" variant="secondary" iconLeft="refresh" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The inline form of the same thing: one line above a form or a table, for
 * when the screen still has content worth showing behind it.
 */
export function ErrorNote({ error, className }: { error: unknown; className?: string }) {
  const described = describeError(error);
  return (
    <p
      role="alert"
      className={cn(
        'flex items-start gap-2 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-small text-danger-ink',
        className,
      )}
    >
      <Icon name="alert" size={14} className="mt-0.5" />
      <span>
        <strong className="font-semibold">{described.title}.</strong> {described.detail}
      </span>
    </p>
  );
}
